import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import classNames from "classnames";
import { Show } from "control-flow-react";
import PropTypes from "kf-proptypes/widgets";

import {
  ICON_BACKGROUND,
  ICON_HOVER_TYPE,
  ICON_PADDING,
  ICON_SHAPE,
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./accordion.css";

export const ACCORDION_SIZE = {
  LARGE: "large",
  BASE: "base",
  MEDIUM: "medium"
};

export const ACCORDION_TYPE = {
  TYPE1: "type1",
  TYPE2: "type2",
  TYPE3: "type3"
};

export const ACCORDION_VARIANT = {
  DEFAULT: "default",
  FILLED: "filled"
};

const ACCORDION_SIZE_MAPPING = {
  [ACCORDION_TYPE.TYPE1]: [
    ACCORDION_SIZE.LARGE,
    ACCORDION_SIZE.BASE,
    ACCORDION_SIZE.MEDIUM
  ],
  [ACCORDION_TYPE.TYPE2]: [
    ACCORDION_SIZE.LARGE,
    ACCORDION_SIZE.BASE,
    ACCORDION_SIZE.MEDIUM
  ],
  [ACCORDION_TYPE.TYPE3]: [
    ACCORDION_SIZE.LARGE,
    ACCORDION_SIZE.BASE,
    ACCORDION_SIZE.MEDIUM
  ]
};

const MAX_ALLOWED_ICONS = {
  [ACCORDION_TYPE.TYPE1]: 3,
  [ACCORDION_TYPE.TYPE2]: 1,
  [ACCORDION_TYPE.TYPE3]: 3
};

const ACCORDION_ICON_PADDING = {
  [ACCORDION_TYPE.TYPE1]: ICON_PADDING.PADDING_4,
  [ACCORDION_TYPE.TYPE2]: ICON_PADDING.PADDING_NONE,
  [ACCORDION_TYPE.TYPE3]: ICON_PADDING.PADDING_4
};

const ACCORDION_ICON_CLICKABLES = {
  [ACCORDION_TYPE.TYPE1]: true,
  [ACCORDION_TYPE.TYPE2]: false,
  [ACCORDION_TYPE.TYPE3]: true
};

const ICON_SIZE_MAPPING = {
  [ACCORDION_TYPE.TYPE1]: {
    [ACCORDION_SIZE.LARGE]: ICON_SIZE.SIZE_16,
    [ACCORDION_SIZE.BASE]: ICON_SIZE.SIZE_12,
    [ACCORDION_SIZE.MEDIUM]: ICON_SIZE.SIZE_12
  },
  [ACCORDION_TYPE.TYPE2]: {
    [ACCORDION_SIZE.LARGE]: ICON_SIZE.SIZE_12,
    [ACCORDION_SIZE.BASE]: ICON_SIZE.SIZE_12,
    [ACCORDION_SIZE.MEDIUM]: ICON_SIZE.SIZE_12
  },
  [ACCORDION_TYPE.TYPE3]: {
    [ACCORDION_SIZE.LARGE]: ICON_SIZE.SIZE_16,
    [ACCORDION_SIZE.BASE]: ICON_SIZE.SIZE_12,
    [ACCORDION_SIZE.MEDIUM]: ICON_SIZE.SIZE_12
  }
};

const handleActivePanels = ({ activeKeys, isPanelOpen, panelId, collapse }) => {
  let _activeKeys;
  if (collapse) {
    _activeKeys = isPanelOpen
      ? [...activeKeys, panelId]
      : activeKeys.filter((key) => key !== panelId);
  } else {
    _activeKeys = isPanelOpen ? [panelId] : [];
  }
  return _activeKeys;
};

const AccordionContext = createContext();

/**
 * A content area which can be collapsed and expanded.
 */
export function Accordion(props) {
  const {
    defaultActiveKey,
    children,
    type = ACCORDION_TYPE.TYPE2,
    size = ACCORDION_SIZE.BASE,
    collapse = false,
    variant = ACCORDION_VARIANT.DEFAULT,
    onChange = () => {}
  } = props;

  const [activeKeys, setActiveKeys] = useState([]);

  const isValidSize = ACCORDION_SIZE_MAPPING[type].includes(size);

  const accordionSize = isValidSize ? size : ACCORDION_SIZE_MAPPING[type][0];

  useEffect(
    function handleActiveKeys() {
      const getActiveKeys = (defaultActiveKey) => {
        const keys = Array.isArray(defaultActiveKey)
          ? defaultActiveKey
          : [defaultActiveKey];
        if (!collapse) return [keys[0]];
        return keys;
      };
      const keys = getActiveKeys(defaultActiveKey);
      setActiveKeys(keys);
    },
    [defaultActiveKey, collapse]
    // Collapse property will not change in normal usage. Only storybook has toggle option
  );

  const contextValue = useMemo(
    function getContextValue() {
      return {
        type,
        size: accordionSize,
        variant: type !== ACCORDION_TYPE.TYPE3 && variant,
        activeKeys,
        setActiveKeys,
        onChange,
        collapse
      };
    },
    [
      type,
      accordionSize,
      variant,
      activeKeys,
      setActiveKeys,
      onChange,
      collapse
    ]
  );

  const accordionListClassName = classNames(styles.accordionList, {
    [styles.accordionListType1]: type === ACCORDION_TYPE.TYPE1,
    [styles.accordionListType2]: type === ACCORDION_TYPE.TYPE2,
    [styles.accordionListType3]: type === ACCORDION_TYPE.TYPE3
  });

  return (
    <div aria-label="Accordion Panel" className={accordionListClassName}>
      <AccordionContext.Provider value={contextValue}>
        {children}
      </AccordionContext.Provider>
    </div>
  );
}

Accordion.propTypes = {
  /** Children of Accordion must be <Accordion.Panel/>  */
  children: PropTypes.node,
  /** Types of accordion */
  type: PropTypes.oneOf([
    ACCORDION_TYPE.TYPE1,
    ACCORDION_TYPE.TYPE2,
    ACCORDION_TYPE.TYPE3
  ]),
  /** Size of accordion */
  size: PropTypes.oneOf([
    ACCORDION_SIZE.LARGE,
    ACCORDION_SIZE.BASE,
    ACCORDION_SIZE.MEDIUM
  ]),
  /** Toggles collapse behaviour */
  collapse: PropTypes.bool,
  /** Key of the default active panel*/
  defaultActiveKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  /** Variants of accordion */
  variant: PropTypes.oneOf([
    ACCORDION_VARIANT.DEFAULT,
    ACCORDION_VARIANT.FILLED
  ]),
  /** Callback function executed when active panel is changed */
  onChange: PropTypes.func
};

export const AccordionPanel = ({
  title,
  children,
  disabled = false,
  panelId = "",
  lazyLoad = false,
  icons = [],
  extra
}) => {
  const contextData = useContext(AccordionContext);

  const contentRef = useRef(null);

  const {
    onChange: parentOnChange,
    activeKeys,
    setActiveKeys,
    type,
    size,
    variant,
    collapse
  } = contextData;

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);

  let isOpen = useMemo(
    function findIsOpen() {
      return !disabled && activeKeys.includes(panelId);
    },
    [disabled, activeKeys, panelId]
  );

  function toggleAccordion(isOpen) {
    if (lazyLoad && isOpen) setIsContentVisible(true);
    if (isOpen) contentRef.current.style.display = "block";
    setIsAccordionOpen(isOpen);
  }

  useEffect(
    function handleOpen() {
      toggleAccordion(isOpen);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen]
  );

  function handleTransitionEnd(event) {
    if (!isAccordionOpen && event.propertyName === "visibility") {
      contentRef.current.style.display = "none";
      if (lazyLoad) setIsContentVisible(false);
    }
  }

  function onChange(isPanelOpen) {
    const newActiveKeys = handleActivePanels({
      activeKeys,
      isPanelOpen,
      panelId,
      collapse
    });
    setActiveKeys(newActiveKeys);
    parentOnChange({ panelId, isPanelOpen, newActiveKeys, collapse });
  }

  function handleClick() {
    if (disabled) return;
    toggleAccordion(!isAccordionOpen);
    onChange(!isAccordionOpen);
  }

  const AccordionOpenIcon = (
    <ArrowIcon
      type={type}
      isOpen={isAccordionOpen}
      disabled={disabled}
      size={size}
    />
  );
  const Content = (
    <HeaderContent
      icons={icons}
      title={title}
      extra={extra}
      type={type}
      size={size}
      disabled={disabled}
      isOpen={isOpen}
    />
  );

  const PANEL_CONTENT = {
    [ACCORDION_TYPE.TYPE1]: [AccordionOpenIcon, Content],
    [ACCORDION_TYPE.TYPE2]: [Content, AccordionOpenIcon],
    [ACCORDION_TYPE.TYPE3]: [AccordionOpenIcon, Content]
  };

  const accordionClassName = classNames(
    "accordion-panel",
    `accordion-${type}`,
    {
      "accordion-open": isAccordionOpen,
      "accordion-disabled": disabled,
      "accordion-enabled": !disabled
    }
  );

  const accordionHeaderClassName = classNames(
    "accordion-panel-header",
    `header-${size}`,
    {
      "accordion-fill": variant === ACCORDION_VARIANT.FILLED
    }
  );

  const tabIndex = getTabIndex(disabled);

  return (
    <div className={accordionClassName} aria-label="Accordion Panel">
      <button
        aria-expanded={isAccordionOpen}
        aria-controls={`#accordion-content-${panelId}`}
        aria-label="Accordion Header"
        tabIndex={tabIndex}
        className={accordionHeaderClassName}
        onClick={handleClick}
        aria-disabled={disabled}
      >
        {PANEL_CONTENT[type]}
      </button>

      <div
        id={`accordion-content-${panelId}`}
        aria-label="Accordion Content"
        aria-hidden={!isAccordionOpen}
        className="accordion-body-wrapper"
      >
        <div onTransitionEnd={handleTransitionEnd} className="accordion-body">
          <div ref={contentRef} className="accordion-body-content">
            <Show when={lazyLoad ? isContentVisible : true}>{children}</Show>
          </div>
        </div>
      </div>
    </div>
  );
};

Accordion.Panel = AccordionPanel;

AccordionPanel.propTypes = {
  /** Unique key identifying the panel from among its siblings	 */
  panelId: PropTypes.string.isRequired,
  /** Accordion title */
  title: PropTypes.node,
  /** Accordion content */
  children: PropTypes.node,
  /** Toggles disabled state */
  disabled: PropTypes.bool,
  /** Lazy rendering of content after clicking on header */
  lazyLoad: PropTypes.bool,
  /** Icons to be rendered in panel */
  icons: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      stroke: PropTypes.string,
      onClick: PropTypes.func
    })
  ),
  /** Extra items to be rendered in panel */
  extra: PropTypes.node
};

AccordionPanel.defaultProps = {
  disabled: false
};

const getTabIndex = (disabled) => {
  if (disabled) return "-1";
  return "0";
};

const HeaderContent = ({
  title,
  icons,
  type,
  size,
  extra,
  disabled,
  isOpen
}) => {
  const allowedIcons = icons.toSpliced(MAX_ALLOWED_ICONS[type]);
  const accordionTitleClassName = classNames("accordion-panel-header-title", {
    base: size === ACCORDION_SIZE.BASE || size === ACCORDION_SIZE.LARGE,
    small: size === ACCORDION_SIZE.MEDIUM
  });

  const Title = <div className={accordionTitleClassName}>{title}</div>;
  const Icons = (
    <Show when={allowedIcons.length > 0}>
      <div className="accordion-panel-header-icons">
        {allowedIcons.map(({ icon, stroke, onClick }) => {
          const isClickable = ACCORDION_ICON_CLICKABLES[type] && !!onClick;

          return (
            <KfIcon
              key={icon}
              icon={icon}
              stroke={stroke || ICON_STROKE.THIN}
              size={ICON_SIZE_MAPPING[type][size]}
              color={disabled ? "--kf-color-gray-300" : "--kf-color-gray-700"}
              padding={ACCORDION_ICON_PADDING[type]}
              onClick={(event) => {
                if (!isClickable) return;
                onClick(event);
                event.stopPropagation();
              }}
              backgroundType={ICON_BACKGROUND.TRANSPARENT}
              hoverType={
                isClickable
                  ? ICON_HOVER_TYPE.SOLID
                  : ICON_HOVER_TYPE.TRANSPARENT
              }
              iconShape={ICON_SHAPE.ROUND}
            />
          );
        })}
      </div>
    </Show>
  );

  const Extra = <div className={"accordion-panel-header-extra"}>{extra}</div>;

  const HEADER_CONTENT = {
    [ACCORDION_TYPE.TYPE1]: [Title, Extra, Icons],
    [ACCORDION_TYPE.TYPE2]: [Icons, Title, Extra],
    [ACCORDION_TYPE.TYPE3]: [Title, Icons]
  };
  return (
    <div className="accordion-panel-header-content">{HEADER_CONTENT[type]}</div>
  );
};

HeaderContent.propTypes = {
  title: PropTypes.node,
  type: PropTypes.oneOf([
    ACCORDION_TYPE.TYPE1,
    ACCORDION_TYPE.TYPE2,
    ACCORDION_TYPE.TYPE3
  ]),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf([
    ACCORDION_SIZE.LARGE,
    ACCORDION_SIZE.BASE,
    ACCORDION_SIZE.MEDIUM
  ]),
  icons: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      stroke: PropTypes.string,
      onClick: PropTypes.func
    })
  ),
  extra: PropTypes.node,
  isOpen: PropTypes.bool
};

const ArrowIcon = ({ isOpen, type, disabled, size }) => {
  const ICONS = {
    [ACCORDION_TYPE.TYPE1]: {
      ICON: KF_ICONS.ARROW_RIGHT_SIMPLE,
      STROKE: ICON_STROKE.THIN,
      SIZE:
        size === ACCORDION_SIZE.LARGE ? ICON_SIZE.SIZE_16 : ICON_SIZE.SIZE_12
    },
    [ACCORDION_TYPE.TYPE2]: {
      ICON: KF_ICONS.ARROW_RIGHT_SIMPLE,
      STROKE: ICON_STROKE.THIN,
      SIZE:
        size === ACCORDION_SIZE.LARGE ? ICON_SIZE.SIZE_16 : ICON_SIZE.SIZE_12
    },
    [ACCORDION_TYPE.TYPE3]: {
      ICON: KF_ICONS.ARROW_RIGHT_FILLED_1,
      STROKE: ICON_STROKE.BOLD,
      SIZE:
        size === ACCORDION_SIZE.LARGE ? ICON_SIZE.SIZE_16 : ICON_SIZE.SIZE_12
    }
  };

  const accordionIconClassName = classNames("accordion-icon", {
    "accordion-icon-open": isOpen
  });

  return (
    <div className={accordionIconClassName}>
      <KfIcon
        icon={ICONS[type]["ICON"]}
        size={ICONS[type]["SIZE"]}
        stroke={ICONS[type]["STROKE"]}
        color={disabled ? "--kf-color-gray-300" : "--kf-color-gray-700"}
      />
    </div>
  );
};

ArrowIcon.propTypes = {
  isOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf([
    ACCORDION_TYPE.TYPE1,
    ACCORDION_TYPE.TYPE2,
    ACCORDION_TYPE.TYPE3
  ]),
  size: PropTypes.oneOf([
    ACCORDION_SIZE.LARGE,
    ACCORDION_SIZE.BASE,
    ACCORDION_SIZE.MEDIUM
  ])
};
